// src/offline/bootstrap.js
import { Global } from "../helpers/Global";

// 🔑 Claves de storage
const CENTROS_JORNADAS_KEY = "pwa.cache.centros";            // ya existente (jornadas)
const ORDENES_KEY          = "pwa.cache.ordenes";            // nuevas (intervenciones)
const CLIENTES_KEY         = "pwa.cache.clientes";           // nuevas (intervenciones)
const CENTROS_POR_OT_KEY   = "pwa.cache.ordenes.centros";    // nuevas (intervenciones)

// Cabeceras con token
const tokenHeader = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Pequeño helper seguro para guardar en localStorage sin petar
const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Podemos loguear si quieres
    // console.warn("[primeOfflineData] No se pudo guardar", key, e);
  }
};

/**
 * 🔄 Igual que en jornadas, pero extendido:
 * - Centros que permiten jornadas
 * - Ordenes activas (pendiente/en curso)
 * - Clientes
 * - Centros por cada OT activa
 *
 * Además, avisa al Service Worker para que caliente todos esos endpoints.
 */
export async function primeOfflineData() {
  const urlsToWarm = [];

  // =========================
  // 1) Centros para JORNADAS
  // =========================
  const urlCentrosJornadas = `${Global.url}centros?permite_jornadas=true`;

  try {
    const res = await fetch(urlCentrosJornadas, {
      headers: tokenHeader(),
      cache: "no-store",
    });
    const isFallback = res.headers.get("X-Offline-Fallback") === "1";

    if (res.ok) {
      const data = await res.json().catch(() => []);
      if (!isFallback && Array.isArray(data) && data.length) {
        safeSet(CENTROS_JORNADAS_KEY, data);
        urlsToWarm.push(urlCentrosJornadas);
      }
    }
  } catch {
    // sin red: no pasa nada, simplemente no actualizamos cache
  }

  // ==========================================
  // 2) Ordenes + Clientes + Centros por OT
  //    (para INTERVENCIONES)
  // ==========================================

  // -- 2.1) Ordenes activas --
  let ordenesActivas = [];
  const urlOrdenes = `${Global.url}ordenes`;

  try {
    const res = await fetch(urlOrdenes, {
      headers: tokenHeader(),
      cache: "no-store",
    });
    const isFallback = res.headers.get("X-Offline-Fallback") === "1";

    if (res.ok) {
      const lista = await res.json().catch(() => []);
      const arr = Array.isArray(lista) ? lista : [];
      // mismo criterio que en IniciarIntervencion
      ordenesActivas = arr.filter(
        (o) =>
          ["pendiente", "en curso"].includes((o.estado || "").toLowerCase()) &&
          o.activo === true
      );
      if (!isFallback && ordenesActivas.length) {
        safeSet(ORDENES_KEY, ordenesActivas);
        urlsToWarm.push(urlOrdenes);
      }
    }
  } catch {
    // sin red
  }

  // -- 2.2) Clientes --
  const urlClientes = `${Global.url}clientes`;
  try {
    const res = await fetch(urlClientes, {
      headers: tokenHeader(),
      cache: "no-store",
    });
    const isFallback = res.headers.get("X-Offline-Fallback") === "1";

    if (res.ok) {
      const data = await res.json().catch(() => []);
      const clientes = Array.isArray(data) ? data : [];
      if (!isFallback && clientes.length) {
        safeSet(CLIENTES_KEY, clientes);
        urlsToWarm.push(urlClientes);
      }
    }
  } catch {
    // sin red
  }

  // -- 2.3) Centros por cada OT activa --
  const centrosPorOT = {};
  for (const ot of ordenesActivas) {
    if (!ot?.id) continue;
    const id = Number(ot.id);
    if (!id) continue;

    const url = `${Global.url}ordenes/${id}/centros`;
    try {
      const res = await fetch(url, {
        headers: tokenHeader(),
        cache: "no-store",
      });
      const isFallback = res.headers.get("X-Offline-Fallback") === "1";

      if (res.ok) {
        const data = await res.json().catch(() => []);
        const listaCentros = Array.isArray(data) ? data : data?.data || [];
        if (!isFallback && listaCentros.length) {
          centrosPorOT[id] = listaCentros;
          urlsToWarm.push(url);
        }
      }
    } catch {
      // si falla una OT, seguimos con las demás
    }
  }

  if (Object.keys(centrosPorOT).length) {
    safeSet(CENTROS_POR_OT_KEY, centrosPorOT);
  }

  // ===============================
  // 3) Avisar al Service Worker
  // ===============================
  if (navigator.serviceWorker?.controller && urlsToWarm.length) {
    // deduplicamos un poco por si acaso
    const uniqueUrls = Array.from(new Set(urlsToWarm));
    navigator.serviceWorker.controller.postMessage({
      type: "WARM_API_CACHE",
      urls: uniqueUrls,
    });
  }
}
